import React, { useState } from "react";
import { Modal, Button, Spinner, Alert } from "react-bootstrap";
import { IProductionDashboardStepDTO } from "../../models/DTO/Production/IProductionResponseDTO";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";

interface MoveSerialsModalProps {
    show: boolean;
    mode: "step" | "inventory";
    selectedCount: number;
    currentStep: IProductionDashboardStepDTO;
    steps: IProductionDashboardStepDTO[];
    terminalStep: IProductionDashboardStepDTO | null;
    isMoving: boolean;
    errorMessage: string | null;
    onConfirm: (stepId: number, notes: string) => void;
    onClose: () => void;
}

const MoveSerialsModal: React.FC<MoveSerialsModalProps> = ({
    show,
    mode,
    selectedCount,
    currentStep,
    steps,
    terminalStep,
    isMoving,
    errorMessage,
    onConfirm,
    onClose,
}) => {
    const [targetStepId, setTargetStepId] = useState<number | null>(null);
    const [notes, setNotes] = useState("");

    const stepOptions = steps.filter((s) => s.stepId != null && s.stepId !== currentStep.stepId);
    const resolvedStepId = mode === "inventory" ? (terminalStep?.stepId ?? null) : targetStepId;

    const handleConfirm = () => {
        if (resolvedStepId == null) return;
        onConfirm(resolvedStepId, notes);
    };

    const handleExited = () => {
        setTargetStepId(null);
        setNotes("");
    };

    return (
        <Modal
            show={show}
            onHide={() => {
                if (!isMoving) onClose();
            }}
            onExited={handleExited}
            backdrop={isMoving ? "static" : true}
            keyboard={!isMoving}
            centered
            size="lg"
            className="pd-move-modal">
            <Modal.Header closeButton={!isMoving}>
                <Modal.Title>{mode === "inventory" ? "Move to Inventory" : "Move to Step"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <strong>{selectedCount}</strong> serial{selectedCount !== 1 ? "s" : ""} currently at{" "}
                    <span className="fw-bold">
                        <OwTranslate id={currentStep.stepName} defaultMessage={currentStep.stepName} />
                    </span>
                    {mode === "inventory" && terminalStep && (
                        <>
                            {" → "}
                            <span className="fw-bold">
                                <OwTranslate id={terminalStep.stepName} defaultMessage={terminalStep.stepName} />
                            </span>
                        </>
                    )}
                </div>

                {errorMessage && <div className="alert alert-danger mb-3">{errorMessage}</div>}

                <Alert variant="warning" className="mb-3">
                    <i className="fas fa-triangle-exclamation me-2" />
                    This is a manual override and will be flagged in the serial&apos;s audit log.
                    {mode === "inventory" && " The serials will be treated as completed and counted in inventory."}
                </Alert>

                {mode === "step" && (
                    <div className="mb-3">
                        <label className="form-label" htmlFor="pd-move-step">
                            Target step
                        </label>
                        <select
                            id="pd-move-step"
                            className="form-select"
                            value={targetStepId ?? ""}
                            onChange={(e) => setTargetStepId(e.target.value ? Number(e.target.value) : null)}>
                            <option value="">Select step...</option>
                            {stepOptions.map((s) => (
                                <option key={s.stepId!} value={s.stepId!}>
                                    {s.sequenceOrder}. {s.stepName}
                                    {s.is_terminal ? " (terminal)" : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="mb-3">
                    <label className="form-label" htmlFor="pd-move-notes">
                        Notes (optional)
                    </label>
                    <textarea
                        id="pd-move-notes"
                        className="form-control"
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Reason for this manual move..."
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isMoving}>
                    Cancel
                </Button>
                <Button variant="warning" onClick={handleConfirm} disabled={isMoving || resolvedStepId == null || selectedCount === 0}>
                    {isMoving ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Moving...
                        </>
                    ) : (
                        "Confirm Move"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MoveSerialsModal;
